import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  X,
  Search,
  Plus,
  Trash2,
  AlertCircle,
  Package,
  Layers,
  ArrowRight,
  RefreshCw,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { shouldCloseDialog, useEscapeClose } from '@/hooks/useEscapeClose'
import { getStockOverview } from '@/services/inventory'
import {
  getFreeStockList,
  formatFreeStockDetails,
} from '@/services/warehouse/freeStockApi'
import {
  createIssueSlip,
  type IssueSlipDetail,
  type CreateIssueSlipLineInput,
} from '@/services/warehouse/issueSlipsApi'

export interface AvailableItem {
  key: string // `MAT:${id}` or `FREE:${id}`
  kind: 'MATERIAL' | 'FREE'
  id: string
  name: string
  uom: string
  available: number
  details: string
}

export interface DraftIssueLine {
  kind: 'MATERIAL' | 'FREE'
  material_id?: string
  free_stock_item_id?: string
  name: string
  uom: string
  available: number
  details: string
  qty: number
  note?: string
  error?: string
}

function buildAvailableItems(materials: any[] = [], freeStock: any[] = []): AvailableItem[] {
  const list: AvailableItem[] = []
  for (const m of materials) {
    const avail = Number(m.available_to_reserve ?? m.available ?? 0)
    list.push({
      key: `MAT:${m.material_id}`,
      kind: 'MATERIAL',
      id: m.material_id,
      name: m.name,
      uom: m.uom_default || 'Đơn vị',
      available: Math.max(0, avail),
      details: m.kind === 'RAW' ? 'Nguyên vật liệu' : 'Phụ liệu',
    })
  }
  for (const f of freeStock) {
    const avail = Number(f.qty_on_hand ?? 0)
    list.push({
      key: `FREE:${f.id}`,
      kind: 'FREE',
      id: f.id,
      name: f.name,
      uom: f.uom,
      available: Math.max(0, avail),
      details: formatFreeStockDetails(f.details),
    })
  }
  return list
}

interface CreateIssueSlipModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (createdSlip: IssueSlipDetail) => void
  initialMaterials?: any[]
  initialFreeStock?: any[]
  initialLines?: DraftIssueLine[]
  initialPurpose?: string
}

export function CreateIssueSlipModal({
  isOpen,
  onClose,
  onSuccess,
  initialMaterials,
  initialFreeStock,
  initialLines,
  initialPurpose,
}: CreateIssueSlipModalProps): React.ReactElement | null {
  const hasInitial = initialMaterials !== undefined || initialFreeStock !== undefined
  const [loadingItems, setLoadingItems] = useState(false)
  const [allItems, setAllItems] = useState<AvailableItem[]>(
    hasInitial ? buildAvailableItems(initialMaterials, initialFreeStock) : []
  )
  const [filterType, setFilterType] = useState<'ALL' | 'MATERIAL' | 'FREE'>('ALL')
  const [searchLeft, setSearchLeft] = useState('')
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())

  // Right pane: Slip form state
  const [purpose, setPurpose] = useState(initialPurpose ?? '')
  const [slipNote, setSlipNote] = useState('')
  const [lines, setLines] = useState<DraftIssueLine[]>(initialLines ?? [])

  const [saving, setSaving] = useState(false)
  const [generalError, setGeneralError] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [activeIdempotencyKey, setActiveIdempotencyKey] = useState<string>('')

  const hasUnsavedChanges = Boolean(purpose.trim() || slipNote.trim() || lines.length)
  const requestClose = useCallback(() => {
    if (saving || !shouldCloseDialog(hasUnsavedChanges)) return
    onClose()
  }, [hasUnsavedChanges, onClose, saving])
  useEscapeClose({ enabled: isOpen, hasUnsavedChanges, onClose: requestClose })

  // Load available materials & free stock items
  useEffect(() => {
    if (!isOpen) return
    if (hasInitial) {
      setAllItems(buildAvailableItems(initialMaterials, initialFreeStock))
      return
    }
    let isMounted = true
    setLoadingItems(true)
    setGeneralError(null)

    // Generate new idempotency key on modal open
    setActiveIdempotencyKey(`slip-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`)

    Promise.all([
      getStockOverview().catch(() => []),
      getFreeStockList().catch(() => []),
    ])
      .then(([materials, freeStock]) => {
        if (!isMounted) return
        const list: AvailableItem[] = []

        // 1. Materials
        for (const m of materials) {
          const avail = Number(m.available_to_reserve ?? m.available ?? 0)
          // Do NOT include material code, as per rule!
          list.push({
            key: `MAT:${m.material_id}`,
            kind: 'MATERIAL',
            id: m.material_id,
            name: m.name,
            uom: m.uom_default || 'Đơn vị',
            available: Math.max(0, avail),
            details: m.kind === 'RAW' ? 'Nguyên vật liệu' : 'Phụ liệu',
          })
        }

        // 2. Free Stock items
        for (const f of freeStock) {
          const avail = Number(f.qty_on_hand ?? 0)
          list.push({
            key: `FREE:${f.id}`,
            kind: 'FREE',
            id: f.id,
            name: f.name,
            uom: f.uom,
            available: Math.max(0, avail),
            details: formatFreeStockDetails(f.details),
          })
        }

        setAllItems(list)
      })
      .finally(() => {
        if (isMounted) setLoadingItems(false)
      })

    return () => {
      isMounted = false
    }
  }, [isOpen])

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPurpose('')
      setSlipNote('')
      setLines([])
      setSelectedKeys(new Set())
      setGeneralError(null)
      setIsDragOver(false)
    }
  }, [isOpen])

  // Filtered left items
  const filteredItems = useMemo(() => {
    return allItems.filter((item) => {
      if (filterType === 'MATERIAL' && item.kind !== 'MATERIAL') return false
      if (filterType === 'FREE' && item.kind !== 'FREE') return false
      if (searchLeft.trim()) {
        const q = searchLeft.toLowerCase()
        const matchName = item.name.toLowerCase().includes(q)
        const matchDetails = item.details.toLowerCase().includes(q)
        if (!matchName && !matchDetails) return false
      }
      return true
    })
  }, [allItems, filterType, searchLeft])

  // Helper to add items to right pane
  function addItemsToDraft(itemsToAdd: AvailableItem[]) {
    setLines((prev) => {
      const next = [...prev]
      for (const it of itemsToAdd) {
        // If already added, focus or skip
        const existingIdx = next.findIndex((l) =>
          it.kind === 'MATERIAL'
            ? l.kind === 'MATERIAL' && l.material_id === it.id
            : l.kind === 'FREE' && l.free_stock_item_id === it.id,
        )
        if (existingIdx >= 0) {
          // Already in list, do not duplicate
          continue
        }
        next.push({
          kind: it.kind,
          material_id: it.kind === 'MATERIAL' ? it.id : undefined,
          free_stock_item_id: it.kind === 'FREE' ? it.id : undefined,
          name: it.name,
          uom: it.uom,
          available: it.available,
          details: it.details,
          qty: 1, // default 1
        })
      }
      return next
    })
  }

  // Handle drag and drop
  function handleDragStart(e: React.DragEvent, item: AvailableItem) {
    e.dataTransfer.setData('application/json', JSON.stringify(item))
    e.dataTransfer.effectAllowed = 'copy'
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setIsDragOver(false)
    try {
      const dataStr = e.dataTransfer.getData('application/json')
      if (!dataStr) return
      const item = JSON.parse(dataStr) as AvailableItem
      if (item && item.id && item.kind) {
        addItemsToDraft([item])
      }
    } catch {
      // ignore
    }
  }

  function handleAddSelected() {
    const selected = allItems.filter((i) => selectedKeys.has(i.key))
    if (selected.length === 0) return
    addItemsToDraft(selected)
    setSelectedKeys(new Set())
  }

  function handleToggleSelect(key: string) {
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function handleSelectAllVisible() {
    if (filteredItems.every((i) => selectedKeys.has(i.key))) {
      setSelectedKeys((prev) => {
        const next = new Set(prev)
        filteredItems.forEach((i) => next.delete(i.key))
        return next
      })
    } else {
      setSelectedKeys((prev) => {
        const next = new Set(prev)
        filteredItems.forEach((i) => next.add(i.key))
        return next
      })
    }
  }

  function handleRemoveLine(index: number) {
    setLines((prev) => prev.filter((_, i) => i !== index))
  }

  function handleLineQtyChange(index: number, valStr: string) {
    const num = parseFloat(valStr)
    setLines((prev) =>
      prev.map((l, i) => {
        if (i !== index) return l
        const qty = Number.isNaN(num) ? 0 : num
        let error: string | undefined = undefined
        if (qty <= 0) {
          error = 'Số lượng phải > 0'
        } else if (qty > l.available) {
          error = `Vượt tồn khả dụng (${l.available.toLocaleString('vi-VN')})`
        }
        return { ...l, qty, error }
      }),
    )
  }

  function handleLineNoteChange(index: number, note: string) {
    setLines((prev) =>
      prev.map((l, i) => (i === index ? { ...l, note } : l)),
    )
  }

  // Handle Save
  async function handleSaveSlip() {
    setGeneralError(null)

    if (!purpose.trim()) {
      setGeneralError('Vui lòng nhập mục đích xuất kho (bắt buộc)')
      return
    }

    if (lines.length === 0) {
      setGeneralError('Phiếu xuất kho phải có ít nhất một dòng hàng')
      return
    }

    let hasInvalidQty = false
    const validatedLines = lines.map((l) => {
      let err = l.error
      if (!l.qty || l.qty <= 0) {
        err = 'Số lượng phải > 0'
        hasInvalidQty = true
      } else if (l.qty > l.available) {
        err = `Vượt tồn khả dụng (${l.available.toLocaleString('vi-VN')})`
      }
      return { ...l, error: err }
    })
    setLines(validatedLines)

    if (hasInvalidQty) {
      setGeneralError('Vui lòng kiểm tra lại số lượng xuất của các dòng')
      return
    }

    const payloadLines: CreateIssueSlipLineInput[] = lines.map((l) => ({
      kind: l.kind,
      material_id: l.kind === 'MATERIAL' ? l.material_id : undefined,
      free_stock_item_id: l.kind === 'FREE' ? l.free_stock_item_id : undefined,
      qty: l.qty,
      note: l.note?.trim() || undefined,
    }))

    // Use current idempotency key, or generate new
    const key = activeIdempotencyKey || `slip-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    setSaving(true)

    try {
      const created = await createIssueSlip(
        {
          purpose: purpose.trim(),
          note: slipNote.trim() || undefined,
          lines: payloadLines,
        },
        key,
      )
      onSuccess(created)
      onClose()
    } catch (err: any) {
      // Map error to specific lines if possible
      const code = err.code || ''
      const msg = err.message || 'Lỗi khi lưu phiếu xuất kho'

      if (code === 'INSUFFICIENT_STOCK' || code === 'INSUFFICIENT_UNRESERVED_STOCK') {
        setGeneralError(`Lỗi tồn kho: ${msg}. Vui lòng kiểm tra các dòng có số lượng vượt tồn.`)
      } else if (code === 'PURPOSE_REQUIRED') {
        setGeneralError('Mục đích xuất kho là bắt buộc')
      } else {
        setGeneralError(`${msg} (${code || 'ERROR'})`)
      }
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 backdrop-blur-xs font-sans"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-issue-slip-title"
    >
      <div className="flex h-[92vh] w-full max-w-[1240px] xl:max-w-7xl lg:min-w-[1100px] flex-col rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-5 py-3.5">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
              <Package className="h-4 w-4" />
            </div>
            <div>
              <h2 id="create-issue-slip-title" className="text-base font-bold text-slate-900">
                Tạo phiếu xuất kho tổng hợp
              </h2>
              <p className="text-xs text-slate-500">
                Kéo thả hoặc chọn nhiều hàng từ kho vật tư và kho hàng tự do vào phiếu xuất
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={requestClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Đóng"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Error message banner */}
        {generalError ? (
          <div className="flex items-center gap-2 border-b border-rose-200 bg-rose-50 px-5 py-2 text-xs text-rose-800">
            <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" />
            <span>{generalError}</span>
          </div>
        ) : null}

        {/* 2-Pane Body */}
        <div className="flex min-h-0 flex-1 flex-col divide-y divide-slate-200 md:flex-row md:divide-x md:divide-y-0">
          {/* KHUNG TRÁI: DANH SÁCH HÀNG ĐỂ CHỌN */}
          <div className="flex flex-col overflow-hidden bg-slate-50/50 p-4 w-full md:w-80 lg:w-[320px] shrink-0">
            <div className="mb-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-700">
                  Hàng khả dụng ({filteredItems.length})
                </span>
                <span className="text-[11px] text-slate-400 italic">
                  Kéo sang phải hoặc chọn tick
                </span>
              </div>

              {/* Type filter tabs */}
              <div className="flex rounded-md border border-slate-200 bg-white p-0.5 text-xs">
                <button
                  type="button"
                  onClick={() => setFilterType('ALL')}
                  className={`flex-1 rounded py-1 text-center font-medium transition-colors ${
                    filterType === 'ALL'
                      ? 'bg-slate-800 text-white'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Tất cả
                </button>
                <button
                  type="button"
                  onClick={() => setFilterType('MATERIAL')}
                  className={`flex-1 rounded py-1 text-center font-medium transition-colors ${
                    filterType === 'MATERIAL'
                      ? 'bg-slate-800 text-white'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Vật tư
                </button>
                <button
                  type="button"
                  onClick={() => setFilterType('FREE')}
                  className={`flex-1 rounded py-1 text-center font-medium transition-colors ${
                    filterType === 'FREE'
                      ? 'bg-slate-800 text-white'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Hàng tự do
                </button>
              </div>

              {/* Search input */}
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
                <input
                  type="text"
                  value={searchLeft}
                  onChange={(e) => setSearchLeft(e.target.value)}
                  placeholder="Tìm tên hàng, quy cách..."
                  className="h-8 w-full rounded border border-slate-200 bg-white pl-8 pr-3 text-xs outline-none focus:border-slate-400"
                />
              </div>

              {/* Multi-select bar */}
              <div className="flex items-center justify-between pt-1 text-xs">
                <label className="inline-flex items-center gap-1.5 cursor-pointer text-slate-600">
                  <input
                    type="checkbox"
                    checked={
                      filteredItems.length > 0 &&
                      filteredItems.every((i) => selectedKeys.has(i.key))
                    }
                    onChange={handleSelectAllVisible}
                    className="rounded border-slate-300 text-slate-800"
                  />
                  <span>Chọn tất cả ({selectedKeys.size})</span>
                </label>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleAddSelected}
                  disabled={selectedKeys.size === 0}
                  className="h-7 text-xs"
                >
                  <Plus className="mr-1 h-3 w-3" />
                  Thêm đã chọn
                </Button>
              </div>
            </div>

            {/* Items list scrollable */}
            <div className="flex-1 overflow-y-auto rounded border border-slate-200 bg-white">
              {loadingItems ? (
                <div className="flex items-center justify-center py-12 text-slate-400 text-xs">
                  <RefreshCw className="mr-2 h-3.5 w-3.5 animate-spin" />
                  Đang tải danh sách tồn kho...
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="py-12 text-center text-xs text-slate-400">
                  Không tìm thấy mặt hàng nào
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredItems.map((item) => {
                    const isSelected = selectedKeys.has(item.key)
                    const isAlreadyInDraft = lines.some((l) =>
                      item.kind === 'MATERIAL'
                        ? l.kind === 'MATERIAL' && l.material_id === item.id
                        : l.kind === 'FREE' && l.free_stock_item_id === item.id,
                    )

                    return (
                      <div
                        key={item.key}
                        draggable={!isAlreadyInDraft}
                        onDragStart={(e) => handleDragStart(e, item)}
                        className={`group flex items-start gap-2.5 p-2.5 text-xs transition-colors ${
                          isAlreadyInDraft
                            ? 'bg-slate-50/70 opacity-60 cursor-not-allowed'
                            : 'cursor-grab hover:bg-slate-50 active:cursor-grabbing'
                        }`}
                      >
                        <input
                          type="checkbox"
                          disabled={isAlreadyInDraft}
                          checked={isSelected}
                          onChange={() => handleToggleSelect(item.key)}
                          className="mt-0.5 rounded border-slate-300"
                        />

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-slate-900 truncate">
                              {item.name}
                            </span>
                          </div>

                          <div className="mt-0.5 text-[11px] text-slate-500 truncate">
                            {item.details || '—'}
                          </div>

                          <div className="mt-1 flex items-center justify-between text-[11px]">
                            <span className="text-slate-600">
                              Tồn: <b className="font-semibold text-emerald-700 tabular-nums">{item.available.toLocaleString('vi-VN')} {item.uom}</b>
                            </span>
                            {!isAlreadyInDraft ? (
                              <button
                                type="button"
                                onClick={() => addItemsToDraft([item])}
                                className="inline-flex items-center gap-0.5 text-slate-500 hover:text-slate-900 font-medium"
                                title="Thêm vào phiếu"
                              >
                                Thêm <ArrowRight className="h-3 w-3" />
                              </button>
                            ) : (
                              <span className="text-[10px] text-slate-400 italic">Đã thêm</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          {/* KHUNG PHẢI: PHIẾU XUẤT ĐANG LẬP */}
          <div
            onDragOver={(e) => {
              e.preventDefault()
              setIsDragOver(true)
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
            className={`flex min-w-0 flex-1 flex-col overflow-hidden p-4 transition-colors ${
              isDragOver ? 'bg-emerald-50/40 ring-2 ring-inset ring-emerald-500' : 'bg-white'
            }`}
          >
            {/* Slip metadata inputs */}
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="min-w-0">
                <label className="mb-1 block text-xs font-bold text-slate-700 whitespace-nowrap">
                  Mục đích xuất kho <span className="text-rose-600">*</span>
                </label>
                <input
                  type="text"
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  placeholder="VD: Xuất sản xuất đơn hàng X, Xuất lắp ráp..."
                  className="h-9 w-full min-w-0 rounded border border-slate-300 px-3 text-xs outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
                />
              </div>

              <div className="min-w-0">
                <label className="mb-1 block text-xs font-bold text-slate-700 whitespace-nowrap">
                  Ghi chú phiếu (tùy chọn)
                </label>
                <input
                  type="text"
                  value={slipNote}
                  onChange={(e) => setSlipNote(e.target.value)}
                  placeholder="Ghi chú thêm về phiếu xuất..."
                  className="h-9 w-full min-w-0 rounded border border-slate-300 px-3 text-xs outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
                />
              </div>
            </div>

            {/* Lines section header */}
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-700">
                  Danh sách hàng xuất ({lines.length} dòng)
                </span>
                {isDragOver ? (
                  <span className="animate-pulse rounded bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                    Thả chuột tại đây để thêm dòng!
                  </span>
                ) : null}
              </div>
              {lines.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setLines([])}
                  className="text-xs text-rose-600 hover:underline"
                >
                  Xóa tất cả
                </button>
              ) : null}
            </div>

            {/* Table of draft lines */}
            <div className="flex-1 overflow-x-auto overflow-y-auto rounded border border-slate-200">
              {lines.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center p-8 text-center text-slate-400">
                  <Layers className="mb-2 h-10 w-10 text-slate-300" />
                  <p className="text-sm font-semibold text-slate-600">
                    Chưa có dòng nào trong phiếu xuất
                  </p>
                  <p className="mt-1 text-xs text-slate-400 max-w-sm">
                    Kéo thả mặt hàng từ khung bên trái sang đây, hoặc chọn tick và nhấn "Thêm đã chọn"
                  </p>
                </div>
              ) : (
                <table className="w-full min-w-[720px] border-collapse text-left text-xs">
                  <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 font-semibold uppercase tracking-wider text-slate-600">
                    <tr>
                      <th className="w-10 px-2.5 py-2 text-center whitespace-nowrap">STT</th>
                      <th className="min-w-[180px] px-2.5 py-2 whitespace-nowrap">Tên hàng / Vật tư</th>
                      <th className="min-w-[180px] px-2.5 py-2 whitespace-nowrap">Chi tiết</th>
                      <th className="w-16 min-w-[60px] px-2.5 py-2 text-center whitespace-nowrap">ĐVT</th>
                      <th className="min-w-[110px] px-2.5 py-2 text-right whitespace-nowrap">Tồn kho</th>
                      <th className="min-w-[120px] px-2.5 py-2 text-right whitespace-nowrap">Số lượng xuất</th>
                      <th className="min-w-[180px] px-2.5 py-2 whitespace-nowrap">Ghi chú dòng</th>
                      <th className="w-10 px-2 py-2 text-center whitespace-nowrap" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {lines.map((line, idx) => {
                      const isOverStock = line.qty > line.available
                      return (
                        <tr
                          key={`${line.kind}-${line.material_id || line.free_stock_item_id}-${idx}`}
                          className={`hover:bg-slate-50/80 ${
                            line.error ? 'bg-amber-50/40' : ''
                          }`}
                        >
                          {/* STT */}
                          <td className="px-2.5 py-2 text-center text-slate-400 tabular-nums whitespace-nowrap">
                            {idx + 1}
                          </td>

                          {/* Tên hàng (no material code!) */}
                          <td className="px-2.5 py-2">
                            <div className="font-semibold text-slate-900 break-words">
                              {line.name}
                            </div>
                            {line.error ? (
                              <div className="mt-0.5 text-[11px] font-medium text-rose-600 break-words">
                                {line.error}
                              </div>
                            ) : null}
                          </td>

                          {/* Chi tiết */}
                          <td className="px-2.5 py-2 text-slate-600 break-words">
                            {line.details || '—'}
                          </td>

                          {/* ĐVT */}
                          <td className="px-2.5 py-2 text-center text-slate-700 whitespace-nowrap">
                            {line.uom}
                          </td>

                          {/* Tồn kho khả dụng của vật tư/hàng này */}
                          <td className="px-2.5 py-2 text-right whitespace-nowrap font-semibold tabular-nums text-emerald-700">
                            {line.available.toLocaleString('vi-VN')}
                          </td>

                          {/* Số lượng xuất: ô số KHÔNG mũi tên và KHÔNG đổi bằng lăn chuột */}
                          <td className="px-2.5 py-2 text-right whitespace-nowrap">
                            <input
                              type="number"
                              min="0.0001"
                              step="any"
                              value={line.qty || ''}
                              onChange={(e) => handleLineQtyChange(idx, e.target.value)}
                              onWheel={(e) => (e.target as HTMLElement).blur()}
                              className={`h-7 w-24 rounded border px-2 text-right text-xs font-semibold tabular-nums outline-none transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                                isOverStock
                                  ? 'border-amber-400 bg-amber-50 text-amber-900 focus:border-amber-500'
                                  : 'border-slate-300 bg-white text-slate-900 focus:border-slate-500'
                              }`}
                            />
                          </td>

                          {/* Ghi chú */}
                          <td className="px-2.5 py-2 min-w-[180px]">
                            <input
                              type="text"
                              value={line.note || ''}
                              onChange={(e) => handleLineNoteChange(idx, e.target.value)}
                              placeholder="Ghi chú dòng..."
                              className="h-7 w-full min-w-[160px] rounded border border-slate-200 bg-white px-2 text-[11px] text-slate-700 outline-none focus:border-slate-400"
                            />
                          </td>

                          {/* Xóa dòng */}
                          <td className="px-2 py-2 text-center whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => handleRemoveLine(idx)}
                              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-rose-600"
                              title="Xóa dòng"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Bottom summary and action */}
            <div className="mt-4 flex flex-col gap-2 border-t border-slate-200 pt-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-xs text-slate-500">
                Tổng số dòng: <b className="text-slate-800">{lines.length}</b> · Tổng số lượng xuất: <b className="text-slate-950 font-black tabular-nums">{lines.reduce((s, l) => s + (l.qty || 0), 0).toLocaleString('vi-VN')}</b>
              </div>

              <div className="flex items-center gap-2">
                <Button variant="secondary" onClick={requestClose} disabled={saving}>
                  Hủy bỏ
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSaveSlip}
                  disabled={saving || lines.length === 0}
                >
                  {saving ? (
                    <>
                      <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      Đang lưu...
                    </>
                  ) : (
                    'Lưu phiếu xuất'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
